import {
  pgTable,
  text,
  integer,
  bigint,
  boolean,
  doublePrecision,
  timestamp,
  pgEnum,
  index,
  type AnyPgColumn,
  jsonb,
  unique,
  check,
} from 'drizzle-orm/pg-core'
import { relations, type SQL, sql } from 'drizzle-orm'
import { createInsertSchema } from 'drizzle-zod'
import { z } from 'zod'
import { currencyEnum } from '../shared/currency-enum'
import { type Unit, unitsEnumArray } from '../units'
import { entities } from '../entities/entities.schema'
import { id, createdAt, updatedAt } from '../utils/columns'
import { receivables } from '../receivables/receivables.schema'
import { vatRateClassEnum } from '../shared/vat-rates.schema'
import { getExternalId } from './get-external-id'
// VA-223: type-only, from @w/utils (its home), not the @w/be-utils barrel.
import type { Equal, Expect } from '@w/utils'
import { i18Key } from '../../i18key'

export const catalogItemTypeEnum = pgEnum('catalog_item_type', ['product', 'service', 'bundle'])
export type CatalogItemType = (typeof catalogItemTypeEnum.enumValues)[number]
export const CATALOG_ITEM_EXTERNAL_ID_MAX_LENGTH = 50
export const CATALOG_GROUP_NAME_MAX_LENGTH = 50

// TODO: check indexes
export const catalogGroups = pgTable(
  'catalog_groups',
  {
    id,
    name: text('name').notNull(),
    parentGroupId: text('parent_group_id').references((): AnyPgColumn => catalogGroups.id),
    path: text('path').notNull().default(sql`gen_random_uuid()`),
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    type: catalogItemTypeEnum('type').notNull(),
    datanorm: jsonb('datanorm').$type<{
      creatorShortName: string
      creationDate: string
    }>(), // indicates that group was imported from DATANORM
    createdAt,
    updatedAt,
  },
  (table) => [
    index('idx_catalog_groups_parent_group_id').on(table.parentGroupId),
    index('idx_catalog_groups_entity_type_name').on(table.entityId, table.type, table.name),
    unique('uq_catalog_groups_entity_type_path').on(table.entityId, table.type, table.path),
  ],
)
export type CatalogGroup = typeof catalogGroups.$inferSelect
export type CatalogGroupInsert = typeof catalogGroups.$inferInsert

const catalogGroupInsertSchema = createInsertSchema(catalogGroups)
export const catalogGroupUserInputSchema = catalogGroupInsertSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: z
      .string()
      .min(1, { message: i18Key('invalid') })
      .max(100, { message: i18Key('invalid') }),
    path: z.string().min(1, { message: i18Key('invalid') }),
  })

/**
 * Markup is a percentage of the purchasing price, stored as a decimal so a selling price can be
 * hit exactly. To reach a selling price of 59,00 € from a 58,95 € purchasing price you need
 * 0,0848 % — whole percent (or even tenths) cannot express that, so the user would be stuck one
 * cent either side of the target. {@link MARKUP_DECIMALS} places keep the derived selling price
 * cent-exact for purchasing prices up to ~10.000 €.
 */
export const MARKUP_MAX_PERCENT = 1000
export const MARKUP_DECIMALS = 4

/** Rounds a markup percentage to the stored precision, avoiding float noise like 0.08481700000001. */
export function roundMarkupPercent(markupPercent: number): number {
  const factor = 10 ** MARKUP_DECIMALS
  return Math.round(markupPercent * factor) / factor
}

/** Selling price derived from the stored purchasing price and markup, in cents. */
export function catalogSellingPriceCents(purchasingPrice: number, markupPercent: number): number {
  return Math.round(purchasingPrice * (1 + markupPercent / 100))
}

/**
 * Inverse of {@link catalogSellingPriceCents}: the markup that makes an item sell for a given
 * price. Users think in selling prices, so the catalog form lets them type one and stores the
 * markup behind it — that keeps the selling price tracking later purchasing-price changes.
 *
 * Selling below cost would need a negative markup, which is not supported; those clamp to 0.
 * Returns undefined when there is no purchasing price to be a percentage of.
 */
export function markupPercentFromSellingPrice(
  purchasingPrice: number,
  sellingPriceCents: number,
): number | undefined {
  if (purchasingPrice <= 0) return undefined
  const exact = (sellingPriceCents / purchasingPrice - 1) * 100
  return roundMarkupPercent(Math.min(Math.max(exact, 0), MARKUP_MAX_PERCENT))
}

/**
 * The markup input renders a fixed number of decimals and re-emits whatever it renders when it
 * loses focus. Taking that echo at face value would coarsen a finer stored markup — 0,0848 %
 * would become 0,1 % on a stray focus+blur, moving a 59,00 € selling price back to 59,01 € — so an
 * echo of the value already on screen is ignored and only a genuine edit is accepted.
 */
export function resolveMarkupFromInput(
  storedMarkupPercent: number,
  inputMarkupPercent: number,
  inputDecimals: number,
): number {
  const edited = roundMarkupPercent(inputMarkupPercent)
  return edited === displayedMarkupPercent(storedMarkupPercent, inputDecimals)
    ? storedMarkupPercent
    : edited
}

/** The stored markup as the input with `inputDecimals` places will render it. */
export function displayedMarkupPercent(markupPercent: number, inputDecimals: number): number {
  const factor = 10 ** inputDecimals
  return Math.round(markupPercent * factor) / factor
}

export const markupPercentSchema = z
  .number()
  .min(0, { message: i18Key('invalid') })
  .max(MARKUP_MAX_PERCENT, { message: i18Key('invalid') })
  .transform(roundMarkupPercent)

export const catalogItems = pgTable(
  'catalog_items',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    externalId: text('external_id')
      .notNull()
      .default(sql`gen_random_uuid()`)
      .$defaultFn(getExternalId),
    name: text('name').notNull(),
    _nameLower: text('_name_lower').generatedAlwaysAs((): SQL => sql`lower(${catalogItems.name})`),
    description: text('description').notNull(),
    purchasingPrice: bigint('purchasing_price', { mode: 'number' }).notNull(),
    currency: currencyEnum('currency').notNull(),
    unit: text('unit').$type<Unit>().notNull(),
    markup: doublePrecision('markup').notNull().default(0),
    // Null only on a bundle whose items carry different rates (a "mixed" bundle).
    vatRateClass: vatRateClassEnum('vat_rate_class'),
    type: catalogItemTypeEnum('type').notNull(),
    groupId: text('group_id')
      .notNull()
      .references(() => catalogGroups.id),
    purchasingPriceOverridden: boolean('purchasing_price_overridden').notNull().default(false),
    markupOverridden: boolean('markup_overridden').notNull().default(false),
    createdAt,
    updatedAt,
  },
  (table) => [
    index('idx_catalog_items_group_id').on(table.groupId),
    index('idx_catalog_items_name_lower_trgm').using('gin', sql`${table._nameLower} gin_trgm_ops`),
    index('idx_catalog_items_external_id_trgm').using('gin', sql`${table.externalId} gin_trgm_ops`),
    unique('idx_catalog_items_entity_external_id').on(table.entityId, table.externalId),
    // Only a bundle of mixed rates has no single class.
    check(
      'ck_catalog_items_vat_rate_class_bundle_only',
      sql`${table.vatRateClass} IS NOT NULL OR ${table.type} = 'bundle'`,
    ),
  ],
)
export type CatalogItem = typeof catalogItems.$inferSelect
export type CatalogItemInsert = typeof catalogItems.$inferInsert

// New table for bundle items relationship
export const catalogBundleItems = pgTable(
  'catalog_bundle_items',
  {
    id,
    bundleId: text('bundle_id')
      .notNull()
      .references(() => catalogItems.id),
    catalogItemId: text('catalog_item_id')
      .notNull()
      .references(() => catalogItems.id),
    quantity: doublePrecision('quantity').notNull(),
    createdAt,
    updatedAt,
  },
  (table) => [
    index('idx_catalog_bundle_items_bundle_id').on(table.bundleId),
    index('idx_catalog_bundle_items_catalog_item_id').on(table.catalogItemId),
    unique('idx_catalog_bundle_items_bundle_catalog_item').on(table.bundleId, table.catalogItemId),
  ],
)
export type CatalogBundleItem = typeof catalogBundleItems.$inferSelect
export type CatalogBundleItemInsert = typeof catalogBundleItems.$inferInsert

/** At most one image per catalog item; binary lives in object storage, metadata here. */
export const catalogItemImages = pgTable(
  'catalog_item_images',
  {
    id,
    catalogItemId: text('catalog_item_id')
      .notNull()
      .references(() => catalogItems.id, { onDelete: 'cascade' }),
    imageKey: text('image_key').notNull(),
    imageUrl: text('image_url').notNull(),
    imageContentType: text('image_content_type').notNull(),
    imageFileName: text('image_file_name').notNull(),
    createdAt,
    updatedAt,
  },
  (table) => [unique('uq_catalog_item_images_catalog_item_id').on(table.catalogItemId)],
)
export type CatalogItemImage = typeof catalogItemImages.$inferSelect
export type CatalogItemImageInsert = typeof catalogItemImages.$inferInsert

/** API/query shape when `image` relation is loaded (optional for backward compatibility). */
export type CatalogItemWithImage = typeof catalogItems.$inferSelect & {
  image?: CatalogItemImage | null
}

export const catalogItemsStaging = pgTable(
  'catalog_items_staging',
  {
    id,
    importSessionId: text('import_session_id').notNull(),
    shardingKey: text('sharding_key').notNull().default('1'),
    entityId: text('entity_id').notNull(),
    externalId: text('external_id').notNull().default(sql`gen_random_uuid()`),
    name: text('name').notNull(),
    description: text('description').notNull(),
    purchasingPrice: bigint('purchasing_price', { mode: 'number' }).notNull(),
    currency: currencyEnum('currency').notNull(),
    unit: text('unit').$type<Unit>().notNull(),
    markup: doublePrecision('markup').notNull().default(0),
    // Null only on a bundle whose items carry different rates (a "mixed" bundle).
    vatRateClass: vatRateClassEnum('vat_rate_class'),
    type: catalogItemTypeEnum('type').notNull(),
    groupId: text('group_id').notNull(),
  },
  (table) => [
    index('idx_staging_import_session_shard').on(table.importSessionId, table.shardingKey),
  ],
)
export type CatalogItemsStaging = typeof catalogItemsStaging.$inferSelect
export type CatalogItemsStagingInsert = typeof catalogItemsStaging.$inferInsert
type CatalogItemsStagingSharedFields = Omit<
  CatalogItemsStagingInsert,
  'shardingKey' | 'importSessionId'
>
type CatalogItemSharedFields = Omit<
  CatalogItemInsert,
  'createdAt' | 'updatedAt' | 'purchasingPriceOverridden' | 'markupOverridden'
>
type _Guard = Expect<Equal<CatalogItemsStagingSharedFields, CatalogItemSharedFields>>

const catalogItemInsertSchema = createInsertSchema(catalogItems)

export const catalogItemUserInputSchema = catalogItemInsertSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: z
      .string()
      .min(1, { message: i18Key('invalid') })
      .max(100, { message: i18Key('invalid') }),
    externalId: z
      .string()
      .max(50, { message: i18Key('invalid') })
      .default(''),
    unit: z.enum(unitsEnumArray, { message: i18Key('invalid') }),
    markup: markupPercentSchema,
    // Null only for a bundle of mixed rates; a bundle's class is derived from its items anyway.
    vatRateClass: z.enum(vatRateClassEnum.enumValues, { message: i18Key('invalid') }).nullish(),
  })

export const catalogItemsRevenueAnalytics = pgTable('catalog_items_revenue_analytics', {
  id,
  catalogItemId: text('catalog_item_id').notNull(),
  entityId: text('entity_id').notNull(),
  invoiceId: text('invoice_id').notNull(),
  paidAt: timestamp('paid_at', { mode: 'date' }),
  quantity: doublePrecision('quantity').notNull(),
  lineAmount: bigint('line_amount', { mode: 'number' }).notNull(),
  currency: currencyEnum('currency').notNull(),
  // Null only on a bundle whose items carry different rates (a "mixed" bundle).
  vatRateClass: vatRateClassEnum('vat_rate_class'),
  jobId: text('job_id'),
  jobTypeId: text('job_type_id'),
  clientId: text('client_id').notNull(),
})
export type CatalogItemsRevenueAnalytics = typeof catalogItemsRevenueAnalytics.$inferSelect
export type CatalogItemsRevenueAnalyticsInsert = typeof catalogItemsRevenueAnalytics.$inferInsert

const catalogItemsRevenueAnalyticsInsertSchema = createInsertSchema(catalogItemsRevenueAnalytics)
export const catalogItemsRevenueAnalyticsUserInputSchema =
  catalogItemsRevenueAnalyticsInsertSchema.omit({
    id: true,
    vatRateClass: true,
  })

export const catalogBundleRevenueAnalytics = pgTable('catalog_bundle_revenue_analytics', {
  id,
  catalogItemId: text('catalog_item_id').notNull(),
  entityId: text('entity_id').notNull(),
  invoiceId: text('invoice_id').notNull(),
  paidAt: timestamp('paid_at', { mode: 'date' }),
  quantity: doublePrecision('quantity').notNull(),
  lineAmount: bigint('line_amount', { mode: 'number' }).notNull(),
  currency: currencyEnum('currency').notNull(),
  // Null only on a bundle whose items carry different rates (a "mixed" bundle).
  vatRateClass: vatRateClassEnum('vat_rate_class'),
  jobId: text('job_id'),
  jobTypeId: text('job_type_id'),
  clientId: text('client_id').notNull(),
})
export type CatalogBundleRevenueAnalytics = typeof catalogBundleRevenueAnalytics.$inferSelect
export type CatalogBundleRevenueAnalyticsInsert = typeof catalogBundleRevenueAnalytics.$inferInsert

const catalogBundleRevenueAnalyticsInsertSchema = createInsertSchema(catalogBundleRevenueAnalytics)
export const catalogBundleRevenueAnalyticsUserInputSchema =
  catalogBundleRevenueAnalyticsInsertSchema.omit({
    id: true,
    vatRateClass: true,
  })

export const catalogItemsAndReceivables = pgTable('catalog_items_and_receivables', {
  id,
  catalogItemId: text('catalog_item_id')
    .notNull()
    .references(() => catalogItems.id),
  receivableId: text('receivable_id')
    .notNull()
    .references(() => receivables.id),
})

export type CatalogItemsAndReceivables = typeof catalogItemsAndReceivables.$inferSelect
export type CatalogItemsAndReceivablesInsert = typeof catalogItemsAndReceivables.$inferInsert

export const catalogItemsAndReceivablesRelations = relations(
  catalogItemsAndReceivables,
  ({ one }) => ({
    catalogItem: one(catalogItems, {
      fields: [catalogItemsAndReceivables.catalogItemId],
      references: [catalogItems.id],
    }),
    receivable: one(receivables, {
      fields: [catalogItemsAndReceivables.receivableId],
      references: [receivables.id],
    }),
  }),
)

export const catalogGroupsRelations = relations(catalogGroups, ({ one, many }) => ({
  parentGroup: one(catalogGroups, {
    fields: [catalogGroups.parentGroupId],
    references: [catalogGroups.id],
  }),
  subGroups: many(catalogGroups),
  items: many(catalogItems),
}))

export const catalogItemsRelations = relations(catalogItems, ({ one, many }) => ({
  group: one(catalogGroups, {
    fields: [catalogItems.groupId],
    references: [catalogGroups.id],
  }),
  image: one(catalogItemImages, {
    fields: [catalogItems.id],
    references: [catalogItemImages.catalogItemId],
  }),
  analytics: many(catalogItemsRevenueAnalytics),
  bundleItems: many(catalogBundleItems, { relationName: 'bundle' }),
  itemsInBundles: many(catalogBundleItems, { relationName: 'bundleItem' }),
}))

export const catalogItemImagesRelations = relations(catalogItemImages, ({ one }) => ({
  catalogItem: one(catalogItems, {
    fields: [catalogItemImages.catalogItemId],
    references: [catalogItems.id],
  }),
}))

export const catalogBundleItemsRelations = relations(catalogBundleItems, ({ one }) => ({
  bundle: one(catalogItems, {
    fields: [catalogBundleItems.bundleId],
    references: [catalogItems.id],
    relationName: 'bundle',
  }),
  catalogItem: one(catalogItems, {
    fields: [catalogBundleItems.catalogItemId],
    references: [catalogItems.id],
    relationName: 'bundleItem',
  }),
}))

export const catalogItemsRevenueAnalyticsRelations = relations(
  catalogItemsRevenueAnalytics,
  ({ one }) => ({
    item: one(catalogItems, {
      fields: [catalogItemsRevenueAnalytics.catalogItemId],
      references: [catalogItems.id],
    }),
    invoice: one(receivables, {
      fields: [catalogItemsRevenueAnalytics.invoiceId],
      references: [receivables.id],
    }),
  }),
)

export const catalogBundleRevenueAnalyticsRelations = relations(
  catalogBundleRevenueAnalytics,
  ({ one }) => ({
    item: one(catalogItems, {
      fields: [catalogBundleRevenueAnalytics.catalogItemId],
      references: [catalogItems.id],
    }),
    invoice: one(receivables, {
      fields: [catalogBundleRevenueAnalytics.invoiceId],
      references: [receivables.id],
    }),
  }),
)

export const catalogItemsImportSchema = createInsertSchema(catalogItems)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    groupId: true,
    entityId: true,
  })
  .extend({
    group: z
      .string()
      .min(1, { message: i18Key('invalid') })
      .optional(),
    unit: z.enum(unitsEnumArray, { message: i18Key('invalid') }),
    vatRateClass: z.enum(vatRateClassEnum.enumValues, { message: i18Key('invalid') }),
    type: z.enum(['product', 'service', 'bundle'], { message: i18Key('invalid') }),
    // Bounded explicitly: the inferred column schema accepts any float, so an out-of-range
    // markup from a CSV would otherwise only fail at the DB, taking the whole chunk with it.
    markup: markupPercentSchema.optional(),
  })
export type CatalogItemImport = z.infer<typeof catalogItemsImportSchema>

export const catalogImportStatusEnum = pgEnum('catalog_import_status', [
  'creating',
  'pending',
  'imported',
  'failed',
])

export const catalogImportSessions = pgTable(
  'catalog_import_sessions',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    status: catalogImportStatusEnum('status'),
    chunksTotal: integer('chunks_total').notNull(),
    totalShards: integer('total_shards').notNull().default(1),
    chunkSize: integer('chunk_size').notNull(),
    processedChunks: integer('processed_chunks').notNull().default(0),
    receivedItems: integer('received_items').notNull().default(0),
    datanorm: jsonb('datanorm').$type<{
      creatorShortName: string
      creationDate: string
    }>(),
    finishedAt: timestamp('finished_at', { mode: 'date' }),
    createdAt,
    updatedAt,
  },
  (table) => [
    index('idx_import_sessions_entity_status').on(table.entityId, table.status),
    index('idx_import_sessions_entity_created').on(table.entityId, table.createdAt),
  ],
)

export type CatalogImportSession = typeof catalogImportSessions.$inferSelect
export type CatalogImportSessionInsert = typeof catalogImportSessions.$inferInsert

export const catalogImportJobs = pgTable(
  'catalog_import_jobs',
  {
    id,
    importSessionId: text('import_session_id')
      .notNull()
      .references(() => catalogImportSessions.id),
    chunkNumber: integer('chunk_number').notNull(),
    shardingKey: text('sharding_key').notNull().default('1'),
    data: jsonb('data')
      .$type<{
        items: CatalogItemImport[]
        datanorm?: {
          creatorShortName: string
          creationDate: string
        }
      }>()
      .notNull(),
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    error: text('error'),
  },
  (table) => [
    index('idx_catalog_import_jobs_session_entity_shard').on(
      table.importSessionId,
      table.entityId,
      table.shardingKey,
    ),
  ],
)

export const catalogImportErrors = pgTable(
  'catalog_import_errors',
  {
    id,
    importSessionId: text('import_session_id').notNull(),
    index: integer('index').notNull(),
    errorMessage: text('error_message').notNull(),
  },
  (table) => [index('idx_catalog_import_errors_session').on(table.importSessionId)],
)

export type CatalogImportErrorsInsert = typeof catalogImportErrors.$inferInsert

export const catalogDeletionStatusEnum = pgEnum('catalog_deletion_status', [
  'pending',
  'running',
  'completed',
  'failed',
])

/**
 * One request to delete a supplier catalogue, and what that request actually did.
 *
 * Deleting a supplier catalogue is a hard delete with no soft-delete column to restore from, so
 * the row is kept after completion: it is the only account of what was removed. Counts are
 * summaries, never item payloads - this is an audit trail, not a shadow backup.
 */
export const catalogDeletionJobs = pgTable(
  'catalog_deletion_jobs',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    /** No foreign key: the job's whole purpose is to delete this group. */
    targetGroupId: text('target_group_id').notNull(),
    targetGroupName: text('target_group_name').notNull(),
    status: catalogDeletionStatusEnum('status').notNull().default('pending'),
    plannedItems: integer('planned_items').notNull().default(0),
    deletedItems: integer('deleted_items').notNull().default(0),
    skippedBundleItems: integer('skipped_bundle_items').notNull().default(0),
    /**
     * Customer-authored items **found** in the subtree - not necessarily kept. Whether they were
     * deleted is `includeCustomerItems`; together the two say what happened to them.
     *
     * Column stays `kept_own_items`, which no longer matches the meaning. Renaming it would cost
     * a migration for cosmetics.
     */
    customerItems: integer('kept_own_items').notNull().default(0),
    /** The user ticked the box asking for their own hand-made items to go too. */
    includeCustomerItems: boolean('include_customer_items').notNull().default(false),
    /** Who asked for it, for the same reason the row is kept at all. */
    requestedBy: text('requested_by'),
    error: text('error'),
    finishedAt: timestamp('finished_at', { mode: 'date' }),
    /**
     * When this job last got work done - a heartbeat, not a row-touched stamp.
     *
     * `updatedAt` cannot serve: it says the row was written, so any future write would quietly
     * extend a dead job's life. Null until the first run claims the job, which is why every
     * reader coalesces it with `createdAt`.
     */
    lastProgressAt: timestamp('last_progress_at', { mode: 'date' }),
    createdAt,
    updatedAt,
  },
  (table) => [
    index('idx_catalog_deletion_jobs_entity_status').on(table.entityId, table.status),
    index('idx_catalog_deletion_jobs_target_group').on(table.targetGroupId),
  ],
)
export type CatalogDeletionJob = typeof catalogDeletionJobs.$inferSelect
export type CatalogDeletionJobInsert = typeof catalogDeletionJobs.$inferInsert
